import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';
import { USAGE_LIMITS_I18N } from 'features/usage-config/constants/usage-meters';
import { useGetLlmModels } from 'features/usage-config/hooks/use-llm-models';
import { IModelRef, IModelRestriction } from 'features/usage-config/types/usage-enforcement.model';

import { groupModels, refKey, refOf } from './model-groups';
import { UsageCardFrame, UsageFieldOrigin } from './usage-card-frame';

const T = USAGE_LIMITS_I18N;
const SHOWN_CHIPS = 6;

interface ModelOption extends IModelRef {
  label: string;
}

interface ModelOptionGroup {
  key: string;
  label: string;
  emptyNote?: string;
  models: ModelOption[];
}

export interface ModelRestrictionCardProps {
  restriction: IModelRestriction;
  onRestrictionChange: (restriction: IModelRestriction) => void;
  readOnly?: boolean;
  /** Undefined on the tenant tab, where the restriction is not inherited from anywhere. */
  inherited?: boolean;
  onFollowTenant?: () => void;
}

/** The choice of models for the tenant or one organization. Off, every model can be used. */
export const ModelRestrictionCard = ({
  restriction,
  onRestrictionChange,
  readOnly,
  inherited,
  onFollowTenant,
}: ModelRestrictionCardProps) => {
  const { t } = useTranslation();
  const { data: models = [] } = useGetLlmModels();
  const [picking, setPicking] = useState(false);
  const groups = useMemo<ModelOptionGroup[]>(
    () =>
      groupModels(
        models,
        (model) => model.is_active !== false,
        t(T.MODEL_RESTRICTION_CUSTOM_EMPTY),
        t
      ).map((group) => ({
        ...group,
        models: group.models.map((model) => ({
          ...refOf(model),
          label: model.model_name_label || model.model_name,
        })),
      })),
    [models, t]
  );
  const labels = useMemo(
    () => new Map(groups.flatMap((group) => group.models.map((model) => [refKey(model), model.label]))),
    [groups]
  );
  const labelFor = (ref: IModelRef) => labels.get(refKey(ref)) ?? ref.model_id;
  // Only a model that can be picked now is shown; one deleted since drops out on the next edit.
  const chosen = restriction.models.filter((ref) => labels.has(refKey(ref)));
  const countKey =
    chosen.length === 1 ? T.MODEL_RESTRICTION_COUNT_ONE : T.MODEL_RESTRICTION_COUNT_OTHER;

  return (
    <UsageCardFrame cardId="model-restriction" label={t(T.MODEL_RESTRICTION_TITLE)}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h3 className="text-sm font-semibold">{t(T.MODEL_RESTRICTION_TITLE)}</h3>
          <p className="mt-1 max-w-prose text-sm leading-relaxed text-muted-foreground">
            {inherited === undefined
              ? t(T.MODEL_RESTRICTION_DESCRIPTION)
              : t(T.MODEL_RESTRICTION_ORGANIZATION_DESCRIPTION)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <UsageFieldOrigin
            field="model_restriction"
            inherited={inherited}
            onFollowTenant={onFollowTenant}
            readOnly={readOnly}
          />
          <Switch
            checked={restriction.enabled}
            onCheckedChange={(enabled) => onRestrictionChange({ ...restriction, enabled })}
            disabled={readOnly}
            aria-label={t(T.MODEL_RESTRICTION_LABEL)}
          />
        </div>
      </div>

      {!restriction.enabled ? (
        <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">
          {t(T.MODEL_RESTRICTION_OFF_NOTE)}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-muted-foreground">{t(T.MODEL_RESTRICTION_ON_NOTE)}</p>
          <div className="flex flex-wrap items-center gap-2">
            {chosen.slice(0, SHOWN_CHIPS).map((ref) => (
              <Badge key={refKey(ref)} variant="outline" className="font-mono text-xs font-normal">
                {labelFor(ref)}
              </Badge>
            ))}
            {chosen.length > SHOWN_CHIPS && (
              <span className="text-xs text-muted-foreground">
                {t(T.CATEGORY_MORE, { count: chosen.length - SHOWN_CHIPS })}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={readOnly}
              onClick={() => setPicking(true)}
            >
              {chosen.length ? t(T.MODEL_RESTRICTION_EDIT) : t(T.MODEL_RESTRICTION_PICK)}
            </Button>
          </div>
          <p
            className={
              chosen.length
                ? 'text-xs text-muted-foreground'
                : 'rounded-sm bg-muted px-3 py-2 text-sm text-destructive'
            }
          >
            {chosen.length
              ? t(countKey, { count: chosen.length })
              : t(T.MODEL_RESTRICTION_EMPTY)}
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t(T.MODEL_RESTRICTION_PLAN_NOTE)}</p>

      {picking && (
        <ModelPickerDialog
          selected={chosen}
          groups={groups}
          onClose={() => setPicking(false)}
          onApply={(picked) => {
            onRestrictionChange({ ...restriction, models: picked });
            setPicking(false);
          }}
        />
      )}
    </UsageCardFrame>
  );
};

interface ModelPickerDialogProps {
  selected: IModelRef[];
  groups: ModelOptionGroup[];
  onClose: () => void;
  onApply: (models: IModelRef[]) => void;
}

const ModelPickerDialog = ({ selected, groups, onClose, onApply }: ModelPickerDialogProps) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<IModelRef[]>(selected);
  const [filter, setFilter] = useState('');
  const term = filter.trim().toLowerCase();
  const matches = (model: ModelOption) =>
    model.model_id.toLowerCase().includes(term) || model.label.toLowerCase().includes(term);
  const pickedKeys = new Set(picked.map(refKey));

  const shown = groups.map((group) => ({ ...group, models: group.models.filter(matches) }));

  const toggle = (model: ModelOption) =>
    setPicked((current) =>
      pickedKeys.has(refKey(model))
        ? current.filter((ref) => refKey(ref) !== refKey(model))
        : [...current, { model_id: model.model_id, is_system: model.is_system }]
    );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(T.MODEL_PICKER_TITLE)}</DialogTitle>
          <DialogDescription>{t(T.MODEL_RESTRICTION_ON_NOTE)}</DialogDescription>
        </DialogHeader>
        <Input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t(T.CATEGORY_FILTER_MODELS)}
          aria-label={t(T.CATEGORY_FILTER_MODELS)}
        />
        <div className="flex max-h-80 flex-col gap-3 overflow-y-auto">
          {shown.map((group) => (
            <div key={group.key} className="flex flex-col gap-1">
              <p className="sticky top-0 bg-background py-1 text-xs font-semibold text-muted-foreground">
                {group.label}
              </p>
              {group.models.length === 0 ? (
                <p className="px-2 text-xs text-muted-foreground">
                  {term ? t(T.NOTHING_FOUND) : group.emptyNote}
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {group.models.map((model) => (
                    <li key={refKey(model)}>
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-muted">
                        <Checkbox
                          checked={pickedKeys.has(refKey(model))}
                          onCheckedChange={() => toggle(model)}
                        />
                        <span className="min-w-0 truncate font-mono text-xs">{model.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <span className="mr-auto text-xs text-muted-foreground">
            {t(T.CATEGORY_SELECTED_COUNT, { count: picked.length })}
          </span>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t(T.CANCEL)}
          </Button>
          <Button type="button" onClick={() => onApply(picked)}>
            {t(T.APPLY)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
